import { Component, OnInit, ViewEncapsulation, inject } from '@angular/core';
import { UntypedFormGroup, UntypedFormBuilder, Validators } from '@angular/forms';
import { ModalController } from '@ionic/angular';

import { CustomNavController } from 'src/shared/providers/navigation/custom-nav-controller';
import { RightSidePanelService } from 'src/shared/providers/providers/right-side-panel';
import { TidyStorage } from 'src/shared/providers/tidy-storage';

import { Util } from 'src/shared/util/util';
import { Client } from 'src/providers/client/client';

import { Loading } from 'src/shared/components/loading/loading';
import { ConfirmPage, ConfirmPageParamsModel } from 'src/pages/confirm/confirm.component';

import { HappinessItemsService } from 'src/shared/services/happiness-items.service';
import { HappinessItem } from 'src/models/happiness-item.model';

@Component({
  templateUrl: 'add-edit-happiness-item.html',
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class AddEditHappinessItemPage implements OnInit {

  private fb = inject(UntypedFormBuilder);
  private navCtrl = inject(CustomNavController);
  private rightSidePanelService = inject(RightSidePanelService);
  private storage = inject(TidyStorage);
  private modalController = inject(ModalController);
  private util = inject(Util);
  private client = inject(Client);
  private happinessItemsService = inject(HappinessItemsService);

  form: UntypedFormGroup;
  submitted = false;
  loaded = false;
  isRightSideContent = true;
  errorMessage = '';
  dialogParams: any;
  propertyItems: { value: number; viewValue: string }[] = [];
  isEditMode = false;
  editingItem: HappinessItem | null = null;

  boundSave = this.save.bind(this);
  boundDelete = this.deleteItem.bind(this);

  constructor() {
    this.form = this.fb.group({
      property_id: ['', Validators.required],
      name: ['', Validators.required],
      can_upsell: [true],
      can_comp: [true],
    });
  }

  async ngOnInit() {
    try {
      this.isRightSideContent = await this.storage.retrieve('dialog-right-side-open') || false;
      this.dialogParams = await this.storage.retrieve('dialog-params');

      this.isEditMode = !!this.dialogParams?.item;
      this.editingItem = this.dialogParams?.item || null;

      this.rightSidePanelService.setDialogPageTitle(this.isEditMode ? 'Edit Offering' : 'Add Offering');

      await this.loadAddresses();

      if (this.isEditMode && this.editingItem) {
        this.form.patchValue({
          property_id: this.editingItem.address?.id,
          name: this.editingItem.name,
          can_upsell: this.editingItem.can_upsell,
          can_comp: this.editingItem.can_comp,
        });
      } else if (this.dialogParams?.addressId) {
        this.form.patchValue({ property_id: this.dialogParams.addressId });
      }

      this.loaded = true;
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to load page';
    }
  }

  async loadAddresses() {
    const addresses = await this.client.getAddresses();
    this.propertyItems = addresses.map(address => ({
      value: address.id,
      viewValue: address.address_name || `${address.address1}${address.address2 ? ` ${address.address2}` : ''}, ${address.city}, ${address.zip}`,
    }));
  }

  @Loading('', true)
  async save() {
    this.errorMessage = '';
    this.submitted = true;
    if (!this.form.valid) {
      return;
    }
    try {
      const payload = this.buildPayload();
      if (await this.isDuplicateName(payload.address_id, payload.name)) {
        this.errorMessage = 'An offering with this name already exists for this property.';
        return;
      }
      if (this.isEditMode && this.editingItem) {
        await this.happinessItemsService.updateHappinessItem(this.editingItem.id, payload);
      } else {
        await this.happinessItemsService.createHappinessItem(payload);
      }
      await this.storage.save('didMakeHappinessItemChanges', true);
      this.util.showSuccess(this.isEditMode ? 'Offering updated successfully.' : 'Offering added successfully.');
      this.goBack();
    } catch (err) {
      this.errorMessage = err?.error?.message || err?.message || 'Failed to save offering';
    }
  }

  // Checks against the current happiness items for the target address to avoid the
  // server-side `(address_id, name)` uniqueness constraint surfacing as a 500.
  private async isDuplicateName(addressId: number, name: string): Promise<boolean> {
    const normalized = name.toLowerCase();
    const existing = await this.happinessItemsService.getHappinessItems(addressId).catch(() => [] as HappinessItem[]);
    return existing.some(item =>
      item.name.toLowerCase() === normalized && item.id !== this.editingItem?.id
    );
  }

  async deleteItem() {
    const confirmParams: ConfirmPageParamsModel = {
      title: 'Delete Offering?',
      body: 'Are you sure you want to delete this offering?',
      backText: 'Go Back',
      confirmText: 'Delete Offering',
      confirmAction: this.confirmDeleteItem.bind(this),
    };
    const confirmationModal = await this.modalController.create({
      component: ConfirmPage,
      componentProps: confirmParams,
      animated: false,
      cssClass: 'confirm-modal',
    });
    confirmationModal.present();
  }

  async confirmDeleteItem() {
    try {
      await this.happinessItemsService.deleteHappinessItem(this.editingItem.id);
      await this.storage.save('didMakeHappinessItemChanges', true);
      this.modalController.dismiss();
      this.util.showSuccess('Offering deleted successfully.');
      this.goBack();
    } catch (err) {
      this.modalController.dismiss();
      this.errorMessage = err?.error?.message || err?.message || 'Failed to delete offering';
    }
  }

  private buildPayload(): Record<string, any> {
    const val = this.form.value;
    return {
      address_id: val.property_id,
      name: (val.name || '').trim(),
      can_upsell: !!val.can_upsell,
      can_comp: !!val.can_comp,
    };
  }

  goBack() {
    if (this.isRightSideContent) {
      this.rightSidePanelService.closeRightSidePanel();
    } else {
      this.navCtrl.back();
    }
  }
}
